import React from "react";
import { Entity } from "@common/types/entity";
import { AccountIdentity } from "@common/helpers/rules";
import { Button } from "@design-system/Button/Button";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import {
    CategoryGlMappingOverrideRow,
    UseCategoryGlMappingGridResult,
} from "./useCategoryGlMappingGrid";
import {
    CategoryGlMappingDirection,
    DefaultsByEntityId,
    getSharedDefaultIdentity,
} from "./categoryGlMappingGrid.utils";
import { CategoryGlMappingOverrideRowView } from "./CategoryGlMappingOverrideRow";
import styles from "./CategoryGlMappingGrid.module.scss";

interface Props {
    grid: UseCategoryGlMappingGridResult;
}

function getSharedDefaultName(
    entityIds: number[],
    direction: CategoryGlMappingDirection,
    defaults: DefaultsByEntityId,
): string {
    const identity = getSharedDefaultIdentity({
        entityIds,
        direction,
        defaults,
    });
    return identity?.name ?? "-";
}

const DefaultRow: React.FC<{
    entityIds: number[];
    entities: Entity[];
    defaults: DefaultsByEntityId;
}> = ({ entityIds, entities, defaults }) => {
    const rowEntities = entities.filter((entity) =>
        entityIds.includes(entity.id),
    );

    return (
        <div className={styles.row}>
            <div className={styles.entitiesCell}>
                <EntityAvatarGroup entities={rowEntities} max={3} size="xs" />
            </div>
            <div className={styles.accountCell}>
                <span className={styles.defaultAccount}>
                    {getSharedDefaultName(entityIds, "in", defaults)}
                </span>
            </div>
            <div className={styles.accountCell}>
                <span className={styles.defaultAccount}>
                    {getSharedDefaultName(entityIds, "out", defaults)}
                </span>
            </div>
            <div className={styles.removeCell} />
        </div>
    );
};

function isDivergentDefaultForRow(
    row: CategoryGlMappingOverrideRow,
    defaults: DefaultsByEntityId,
): boolean {
    if (row.entityIds.length < 2) {
        return false;
    }
    const hasSharedIn =
        getSharedDefaultIdentity({
            entityIds: row.entityIds,
            direction: "in",
            defaults,
        }) !== null;
    const hasSharedOut =
        getSharedDefaultIdentity({
            entityIds: row.entityIds,
            direction: "out",
            defaults,
        }) !== null;
    return !hasSharedIn && !hasSharedOut;
}

export const CategoryGlMappingGrid: React.FC<Props> = ({ grid }) => {
    const {
        rows,
        defaultRowEntityIds,
        entities,
        accounts,
        defaults,
        isLoading,
        addRow,
        removeRow,
        setRowEntities,
        setRowAccount,
    } = grid;

    return (
        <div className={styles.grid}>
            <header className={styles.title}>Chart of accounts</header>
            <div className={styles.table}>
                <div className={styles.headerRow}>
                    <div>Entities</div>
                    <div>If cash in</div>
                    <div>If cash out</div>
                    <div />
                </div>

                <DefaultRow
                    entityIds={defaultRowEntityIds}
                    entities={entities}
                    defaults={defaults}
                />

                {rows.map((row) => (
                    <CategoryGlMappingOverrideRowView
                        key={row.id}
                        row={row}
                        accounts={accounts}
                        accountsLoading={isLoading}
                        isDivergentDefault={isDivergentDefaultForRow(
                            row,
                            defaults,
                        )}
                        onEntitiesChange={(entityIds) =>
                            setRowEntities(row.id, entityIds)
                        }
                        onAccountChange={(
                            direction: CategoryGlMappingDirection,
                            identity: AccountIdentity | null,
                        ) => setRowAccount(row.id, direction, identity)}
                        onRemove={() => removeRow(row.id)}
                    />
                ))}

                <div className={styles.addRow}>
                    <Button
                        variant="primary"
                        size="md"
                        type="button"
                        onClick={addRow}
                    >
                        + Add
                    </Button>
                </div>
            </div>
        </div>
    );
};
